import { Component, Input, Output, EventEmitter, ElementRef, ViewChild, OnDestroy } from '@angular/core';
import {FileUpload } from '../container';
import { MatDialog } from '@angular/material/dialog';
import { UploadpopupComponent } from '../uploadpopup/uploadpopup.component';
import { SearchService } from '../search.service';
import { UploadService } from '../upload.service';
import { ConfirmingDialogComponent } from '../confirming-dialog/confirming-dialog.component';
import { getDownloadURL, getStorage, ref } from 'firebase/storage';
import { FirestoreService } from '../firestore-service.service';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'app-uploadfile',
    templateUrl: './uploadfile.component.html',
    styleUrls: ['./uploadfile.component.scss'],
    standalone: true,
    imports: [CommonModule, FormsModule, UploadpopupComponent, ConfirmingDialogComponent],
})
export class UploadfileComponent implements OnDestroy {
  @Input()organizationKey: string = "";
  @Input()userId: string = "";
  @Input()projectKey: string = "";
  @Input()projectName: string = "";
  @Input()contentKey: string = "";
  @Input()newContent: boolean = false;
  @Input()contentType: string = ""; // post / workpackage / comment
  @Input()showCaptureButton: boolean = true;
  @Input()userImageUpload = false // indicates if this is user image upload
  //@Input()channelKey: string = "";
  @Input()newComment: boolean = true;
  @Input()doAddCover: boolean = true
  @Input()objectType: string = "content";
  @Input()imageOnly: boolean = false;
  @Input() embbeddingModel: string = "gemini-embedding-001"
  @Input() captureImageImmediately: boolean = false // indicates if camera should be opened immediately after component is loaded
  @Input() closeAfterImageCapture: boolean = false // indicates if dialog should be closed after image is captured
  @Input() useTextButtons: boolean = false
  @Input() isVideoRecordingAllowed: boolean = true
  @Output() sendCoverImageUpdate = new EventEmitter<string>()
  @Output() isFileUploaded = new EventEmitter<{"isFileUploaded": boolean, "url": string, "storageUrl": string}>();
  @Output() orderGenerateDescription = new EventEmitter<string>();
  @Output() addHTMLField = new EventEmitter<{"content": string}>();
  @ViewChild('videoElement') videoElementRef!: ElementRef<HTMLVideoElement>;
  @ViewChild('audioElement') audioElementRef!: ElementRef<HTMLAudioElement>;
  currentFileUpload?: FileUpload;
  selectedFiles?: FileList;
  percentage = 0;
  dialogRef: any
  @ViewChild('fileInput') inputVariable!: ElementRef; // I need to clean input after selecting file, cause it will not when I reselect file twice

  capturedImage: string | ArrayBuffer | null = null;
  videoStream: MediaStream | null = null;
  isMobile: boolean = false;
  errorMsg: string = '';
  loaderMessage: string = ""
  isLoadingLabelVisible: boolean = false
  imageWasCaptured = false
  audioTranscription = ""
  fileType = ""
  isAudioRecording = false

  stream!: MediaStream;
  private mediaRecorder!: MediaRecorder;
  private audioChunks: BlobPart[] = []; 
  isCameraRecording = false;
  

  constructor(private dialog: MatDialog, private search: SearchService, private uploadService: UploadService, private firestore: FirestoreService) {
  }

  ngOnInit(): void {
    this.isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  }
  
  ngAfterViewInit(): void {
    if (this.captureImageImmediately) {
      this.requestCameraPermissions();
    }
  }

  ngOnDestroy(): void {
    this.stopVideoStream();
  }

  selectFile(event: any): void {
    this.imageWasCaptured = true; // hide the camera
    this.stopVideoStream(); // Stop the video stream after capturing the image
    this.selectedFiles = event.target.files; // Get the file list
  
    if (this.selectedFiles && this.selectedFiles.length > 0) { // Check if there are any selected files
      // Convert the FileList to an Array
      const filesArray = Array.from(this.selectedFiles);
    
      // Start processing files sequentially
      const maxFiles = 10
      if (filesArray.length < maxFiles) {
        this.processFilesSequentially(filesArray, 0);
      } else {
        this.firestore.showSnackBar("You can upload maximum 10 files at once.")
      }
    }
  
    this.inputVariable.nativeElement.value = ''; // Reset input element
  }
  
  private processFilesSequentially(files: File[], index: number): void {
    if (index >= files.length) {
      return; // Exit condition, all files processed
    }
  
    const file = files[index];
    
    // Open dialog for the current file
    this.dialogRef = this.dialog.open(UploadpopupComponent, {
      panelClass: "dialog-container",
      data: {
        "userId": this.userId,
        "contentKey": this.contentKey,
        "file": file,
        "organizationKey": this.organizationKey,
        "projectKey": this.projectKey,
        "contentType": this.contentType,
        "doAddCover": this.doAddCover,
        "objectType": this.objectType,
        "projectName": this.projectName,
        "userImageUpload": this.userImageUpload,
        "coverImageURL": "",
        "closeAfterImageCapture": this.closeAfterImageCapture
      }
    });
  
    this.dialogRef.afterClosed().subscribe((result: any) => {
      if (result != false && (result.documentKey != undefined || this.userImageUpload == true)) {
        const documentKey = result.documentKey;
        this.fileType = file.type;
        const url = result.coverImageURL;
        if (this.fileType.startsWith("application/vnd.openxmlformats") || this.fileType.startsWith("application/msword") || this.fileType.startsWith("application/vnd.ms-excel") || this.fileType.startsWith("application/vnd.ms-powerpoint")) {
          this.extractTextFromDocx(url).then((text: any) => {
          })
        }

      //  if (this.fileType.startsWith("audio")) { // if file is audio, extract transcription
      //   // const path = result.coverImageURL;
      //  }
  
        this.isFileUploaded.emit({ "isFileUploaded": true, "url": result.coverImageURL, "storageUrl": result.coverImageURL });
        
        if (result.coverImageURL != "" && result.coverImageURL != undefined) {
          this.sendCoverImageUpdate.emit(result.coverImageURL);
        }
        if (this.userImageUpload == false) {
            this.search.readDocument(this.organizationKey, this.userId, this.projectKey, this.contentKey, documentKey, this.embbeddingModel, result.fileType)
        } 
      }
  
      // Once the dialog for the current file is closed, process the next file
      this.processFilesSequentially(files, index + 1);
    });
  }

  async extractTextFromDocx(url: string) {
    const text = await this.search.extractTextFromOfficeFiles(url);
    return text;
  }

  async requestCameraPermissions(): Promise<void> {
    try {
      // Specify the 'facingMode' constraint to request front camera
      const constraints = { video: { facingMode: 'environment' }, audio: true, autoplay: true, playinline: true };
      this.videoStream = await navigator.mediaDevices.getUserMedia(constraints);
      setTimeout(() => {
        this.openCamera();
      }, 500);
    } catch (error) {
      this.errorMsg = 'Camera permissions denied or not available.';
    }
    
  }

  openCamera() {
    if (this.videoStream) {
      const videoElement = document.querySelector('video') as HTMLVideoElement;
      if (videoElement) {
        videoElement.srcObject = this.videoStream;
        videoElement.onloadedmetadata = () => {
          videoElement.play().catch(error => {
            this.errorMsg = 'Error playing the video: '+error;
          });
        this.imageWasCaptured = true
        }
      }
    } else {
      this.errorMsg = 'This feature is only available on mobile devices.';
    }
  }

  startRecording(isAudio: boolean = false) {
    this.isAudioRecording = isAudio;
    this.mediaRecorder = null as any;
    navigator.mediaDevices.getUserMedia({ video: isAudio ? false : true, audio: true})
      .then(stream => {
        this.stream = stream;
        setTimeout(() => {
          this.assignStreamToElement();
        }, 0);
        this.mediaRecorder = new MediaRecorder(this.stream);
        this.mediaRecorder.start(1000); // it must be added because of the bug on iPhone
        this.isCameraRecording = true;
        this.audioChunks = []; // Ensure audio chunks are initialized.
        this.mediaRecorder.ondataavailable = (event) => {
          this.audioChunks.push(event.data);
        };
        this.mediaRecorder.onstop = () => {
          const videoBlob = new Blob(this.audioChunks, { type: 'video/webm' });
          this.audioChunks = [];
          const file = new File([videoBlob], 'recorded-video-'+this.contentKey+'-'+new Date().getTime()+'.webm', { type: 'video/webm' });
          
          // Upload video file and process it.
          this.uploadFile(file, this.projectName, this.contentKey).then(url => {
           // this.isCameraRecording = false;
            this.stopRecording();
          }).catch((error: any) => {
            console.error("Error uploading video file: " + error);
            this.stopRecording();
          }); 
        };
      })
      .catch(err => {
        console.error('Error accessing microphone and camera', err);
        this.stopRecording();
      });
  }

  stopRecording() {
    this.mediaRecorder.stop();
  //  this.isCameraRecording = false;
 //   if (this.stream) {        // Stop the stream tracks so the microphone is released
 //       this.stream.getTracks().forEach(track => track.stop());
 ///       this.stream = undefined as any; // Or null
 //     }
      this.stopVideoStream();
}

  async captureImage(): Promise<void> {
    this.imageWasCaptured = true
    const video = document.querySelector('video') as HTMLVideoElement;
    if (video) {
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (context) {
        const aspectRatio = video.videoWidth / video.videoHeight;
        canvas.width = 1200// video.videoWidth;// 
        canvas.height = canvas.width / aspectRatio//video.videoHeight;//
        //canvas.width = video.videoWidth;
        //canvas.height = video.videoHeight;
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        const mimeType = 'image/png';
        this.capturedImage = canvas.toDataURL(mimeType);
        const filename = this.generateRandomFilename(10)+"_capturedimage.png";
        const blob = this.dataURLtoBlob(this.capturedImage);
        const file = new File([blob], filename, { type: 'image/png' });
        const documentKey = this.firestore.generateUID()
        this.uploadFile(file, this.projectName, documentKey);
        this.stopVideoStream(); // Stop the video stream after capturing the image
      }
    }
  }

  dataURLtoBlob(dataURL: string): Blob { // converts dataURL to blob
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)![1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  }

  generateRandomFilename(length: number): string {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    const charactersLength = characters.length;

    for (let i = 0; i < length; i++) {
        const randomIndex = Math.floor(Math.random() * charactersLength);
        result += characters.charAt(randomIndex);
    }
    return result;
  }

  stopVideoStream(): void {
    if (this.videoStream) {
      this.videoStream.getTracks().forEach(track => track.stop());
      this.videoStream = null;
    }
    this.imageWasCaptured = false;
    this.isCameraRecording = false;
    this.isAudioRecording = false;
  }

  arrayBufferToFile(arrayBuffer: any, filename: string, mimeType: string): File {
    const blob = new Blob([arrayBuffer], { type: mimeType });
    const file = new File([blob], filename, { type: mimeType });
    return file;
  }

  async uploadFile(file: File, projectName: string, documentKey: string) { // used when capturing image from camera
    this.fileType = file.type
    let currentFileUpload = new FileUpload(file, file.name, this.contentKey);
    currentFileUpload.url = ""
          const uploadTask = this.uploadService.pushFileToStorage(this.userId, documentKey, currentFileUpload, this.organizationKey)
          let percentage = 0
          uploadTask.on('state_changed',
            (snapshot) => {
              percentage = Math.round(snapshot.bytesTransferred / snapshot.totalBytes)*100; // Track progress of file upload
            },
            (error) => {  // A full list of error codes is available at https://firebase.google.com/docs/storage/web/handle-errors
              switch (error.code) {
                case 'storage/unauthorized':
                  console.error(error)
                  // User doesn't have permission to access the object
                  console.error("User doesn't have permission to access the object")
                  this.dialog.open(ConfirmingDialogComponent, {
                    data: {"content": "Uploaded file is too big. Currently maximum size is 10MB. Contact as at hello@prodely.com to increase the limit.", "onlyCloseButton": true}
                  });
                  this.dialogRef.close(false)
                  this.loaderMessage = "Uploaded file is too big."
                  this.isLoadingLabelVisible = false
                  break;
                case 'storage/canceled':
                  // User canceled the upload
                  console.error("// User canceled the upload")
                  break;
                case 'storage/unknown':
                  // Unknown error occurred, inspect error.serverResponse
                  console.error("Unknown error occurred, inspect error.serverResponse")
                  break;
              }
            }, 
            () => {
                const coverImageURL = uploadTask.snapshot.metadata.fullPath // Upload completed successfully, now we can get the download URL
                const storage = getStorage();
                const imageRef = ref(storage, coverImageURL);
                getDownloadURL(imageRef)
                  .then((url) => {
                    this.loaderMessage = "Content is extracted, saving it to database..."
                    if (this.userImageUpload == false) {
                    this.firestore.addImage(this.organizationKey, this.projectKey, this.contentKey, this.contentType, this.userId, uploadTask.snapshot.metadata.name, uploadTask.snapshot.metadata.fullPath, uploadTask.snapshot.metadata.contentType as string, this.doAddCover, "", projectName, this.objectType).then((result) => {
                    this.firestore.showSnackBar("Captured image has been uploaded.")
                    this.capturedImage = null
                    this.imageWasCaptured = false
                 //   if (this.closeAfterImageCapture == true) {
                      this.isFileUploaded.emit({"isFileUploaded": true, "url": url, "storageUrl": url})
                   // }
                   if (this.fileType && this.fileType.startsWith("image")) { // if image, send message to generate description
                    this.orderGenerateDescription.emit(url)
                   }
                    if (result) {
                      if (this.fileType && this.fileType.startsWith("image")) {
                        this.firestore.updateContentCoverImage(this.organizationKey, this.contentKey, url).then(() => {
                          this.firestore.showSnackBar("Cover image has been updated.")
                        })
                      }
                    }
                  }) 
                } else { // if user uploads its profile image
                  this.capturedImage = null
                  this.imageWasCaptured = false
                  this.isFileUploaded.emit({"isFileUploaded": true, "url": url, "storageUrl": url})
                }               
                })
            }
          )
  }

  assignStreamToElement() {
    if (this.videoStream) {
      if (!this.isAudioRecording && this.videoElementRef) {
        const videoElement = this.videoElementRef.nativeElement;
        videoElement.srcObject = this.videoStream;
        videoElement.muted = true; // Set muted property explicitly
      } else if (this.isAudioRecording && this.audioElementRef) {
        const audioElement = this.audioElementRef.nativeElement;
        audioElement.srcObject = this.videoStream;
        audioElement.muted = true; // Set muted property explicitly
      }
    }
  }
}
